import { useMemo } from 'react';
import { Target, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/hooks/useCollection';
import { useDecks } from '@/hooks/useDecks';
import { useWishlist } from '@/hooks/useWishlist';
import { useLearnProgress } from '@/hooks/useLearnProgress';
import { useMyMatches } from '@/hooks/useMatches';
import { useCards } from '@/hooks/useCards';
import { collectionLineValue } from '@/lib/collectionValue';

const GOAL_LABELS: Record<string, string> = {
  build_deck: 'Build my first deck',
  complete_set: 'Complete a set',
  trade: 'Trade cards',
  learn_rules: 'Learn the rules',
  win_event: 'Win a local event',
  track_value: 'Track collection value',
};

function computeProgress(
  goal: string,
  ctx: {
    totalCards: number;
    uniqueCards: number;
    decks: any[];
    wishlistCount: number;
    duplicateCount: number;
    learnCount: number;
    wins: number;
    estimatedValue: number;
    bestSet: { name: string; have: number; total: number } | null;
  },
): { pct: number; label: string; hash?: string } {
  const legalDecks = ctx.decks.filter(d => (d.total_cards ?? 0) === 60).length;
  switch (goal) {
    case 'build_deck': {
      const have = legalDecks;
      const pct = Math.min(100, (have / 1) * 100);
      return {
        pct,
        label: have > 0 ? `${have} legal deck${have === 1 ? '' : 's'}` : `${ctx.decks[0]?.total_cards ?? 0}/60 cards`,
        hash: '#collection/decks',
      };
    }
    case 'complete_set': {
      const b = ctx.bestSet;
      const pct = b ? Math.round((b.have / Math.max(1, b.total)) * 100) : 0;
      return {
        pct,
        label: b ? `${b.name}: ${b.have}/${b.total}` : 'Scan a card to start',
        hash: '#collection/sets',
      };
    }
    case 'trade': {
      const target = 1;
      const did = ctx.duplicateCount > 0 ? 1 : 0;
      const pct = Math.min(100, ((did + Math.min(1, ctx.wishlistCount / 5)) / (target + 1)) * 100);
      return {
        pct,
        label: ctx.duplicateCount > 0 ? `${ctx.duplicateCount} extras to trade` : 'Add cards to wishlist',
        hash: '#community',
      };
    }
    case 'learn_rules': {
      const target = 8;
      const pct = Math.min(100, (ctx.learnCount / target) * 100);
      return { pct, label: `${ctx.learnCount}/${target} lessons`, hash: '#learn' };
    }
    case 'win_event': {
      const target = 3;
      const pct = Math.min(100, (ctx.wins / target) * 100);
      return { pct, label: `${ctx.wins}/${target} wins`, hash: '#profile' };
    }
    case 'track_value': {
      const target = 50;
      const pct = Math.min(100, (ctx.totalCards / target) * 100);
      return {
        pct,
        label:
          ctx.estimatedValue > 0
            ? `$${ctx.estimatedValue.toFixed(0)} · ${ctx.totalCards} cards`
            : `${ctx.totalCards}/${target} cards`,
        hash: '#collection/collection',
      };
    }
    default:
      return { pct: 0, label: '' };
  }
}

export function GoalProgressDashboard() {
  const { profile } = useAuth();
  const { data: collection } = useCollection();
  const { data: decks } = useDecks();
  const { data: wishlist } = useWishlist();
  const { data: cards } = useCards();
  const { data: matches } = useMyMatches();
  const learn = useLearnProgress();

  const goals: string[] = ((profile as any)?.goals as string[] | undefined) ?? [];

  const ctx = useMemo(() => {
    const totalCards = (collection || []).reduce((s, c) => s + (c.quantity || 1), 0);
    const uniqueCards = (collection || []).length;
    let estimatedValue = 0;
    let duplicateCount = 0;
    for (const item of collection || []) {
      estimatedValue += collectionLineValue(item);
      if ((item.quantity || 1) > 4) duplicateCount += (item.quantity || 1) - 4;
    }
    // Best set completion
    const totalsBySet = new Map<string, number>();
    for (const c of cards || []) {
      if (!c.set_name) continue;
      totalsBySet.set(c.set_name, (totalsBySet.get(c.set_name) || 0) + 1);
    }
    const haveBySet = new Map<string, Set<string>>();
    for (const item of collection || []) {
      const sn = item.card?.set_name;
      if (!sn || !item.card) continue;
      if (!haveBySet.has(sn)) haveBySet.set(sn, new Set());
      haveBySet.get(sn)!.add(item.card.id);
    }
    let bestSet: { name: string; have: number; total: number } | null = null;
    for (const [name, total] of totalsBySet) {
      const have = haveBySet.get(name)?.size ?? 0;
      const pct = have / Math.max(1, total);
      if (!bestSet || pct > bestSet.have / Math.max(1, bestSet.total)) {
        bestSet = { name, have, total };
      }
    }
    const wins = (matches || []).filter(
      (m: any) => m.winner_id && m.winner_id === profile?.user_id,
    ).length;
    return {
      totalCards,
      uniqueCards,
      decks: decks || [],
      wishlistCount: wishlist?.length ?? 0,
      duplicateCount,
      learnCount: learn.total,
      wins,
      estimatedValue,
      bestSet,
    };
  }, [collection, decks, wishlist, cards, matches, learn.total, profile?.user_id]);

  if (!profile || goals.length === 0) return null;

  return (
    <div className="mt-3 rounded-xl border border-border bg-card p-4">
      <h3 className="text-xs font-medium text-foreground font-body mb-3 flex items-center gap-1.5">
        <Target className="h-3.5 w-3.5 text-primary" /> Goal Progress
      </h3>
      <div className="space-y-2.5">
        {goals.map(g => {
          const p = computeProgress(g, ctx);
          return (
            <button
              key={g}
              onClick={() => p.hash && (window.location.hash = p.hash)}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between text-[11px] font-body mb-1">
                <span className="text-foreground font-medium flex items-center gap-1">
                  {GOAL_LABELS[g] || g}
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <span className="text-muted-foreground">{p.label} · {Math.round(p.pct)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${p.pct}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
